<!-- EntityHoldings.vue -->
<template>
  <Stack class="stk-py-8">
    <LoaderWithText v-if="!hasFetchedHoldings || isFetchingHoldings">
      Loading…
    </LoaderWithText>

    <!-- Improved Centering for Empty Holdings Panel -->
    <div
      v-else-if="!showHoldingsArea && entityUploadedDfAgg.length === 0"
      class="tw-flex tw-justify-center tw-items-center"
    >
      <Callout status="info" class="tw-p-8">
        <Stack>
          <!-- Panel Header - Improved centering -->
          <div class="tw-mx-auto tw-text-center tw-space-y-2">
            <Title>This client has no holdings</Title>
            <Callout v-if="!canEdit" status="danger">
              You don't have permission to add holdings. Please contact an
              administrator.
            </Callout>
            <Subtitle v-else>
              Choose <em>one</em> of the three methods below to add holdings.
            </Subtitle>
          </div>

          <!-- Cards Container - Improved layout structure -->
          <div
            v-if="canEdit"
            class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4 tw-justify-items-center stk-color-text-normal"
          >
            <!-- Copy from Portfolio -->
            <Stack
              gap="4"
              class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4"
            >
              <div class="tw-flex tw-items-center tw-gap-3">
                <div
                  class="tw-bg-blue-100 tw-text-blue-600 tw-p-2 tw-rounded-full"
                >
                  <Icon icon="fas fa-copy" />
                </div>
                <h3 class="tw-font-semibold">Copy Holdings from Portfolio</h3>
              </div>

              <div class="tw-flex-grow">
                <PortfolioSearchSelect
                  placeholder="Search portfolios …"
                  @selected="handleSelected"
                  class="tw-w-full"
                />
              </div>

              <div v-if="copyPortfolioLoading">
                <div class="tw-flex tw-justify-center tw-items-center">
                  Copying holdings from portfolio...
                </div>
                <br />
                <div class="tw-flex tw-justify-center tw-items-center">
                  <i class="fa fa-spinner fa-spin fa-2x tw-text-gray-500"></i>
                </div>
              </div>
            </Stack>

            <!-- Upload CSV -->
            <div class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
              <UploadHoldingsCSV
                :entity="entity"
                :can-edit="canEdit"
                @uploaded="populateEntityHoldings"
              />
            </div>

            <!-- Add Manually -->
            <Stack
              gap="4"
              class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4"
            >
              <div class="tw-flex tw-items-center tw-gap-3">
                <div
                  class="tw-bg-blue-100 tw-text-blue-600 tw-p-2 tw-rounded-full"
                >
                  <Icon icon="fas fa-edit" />
                </div>
                <h3 class="tw-font-semibold">Add Manually</h3>
              </div>
              <div
                class="tw-flex-grow tw-flex tw-flex-col tw-items-center tw-justify-center"
              >
                <Stack gap="2" class="tw-text-center">
                  <Button
                    @click="startManualEntry"
                    variant="primary"
                    size="3_5"
                    class="tw-w-full"
                  >
                    Add Holdings Manually
                  </Button>
                  <p class="tw-text-sm tw-text-gray-600 tw-mb-4">
                    Create a new portfolio by adding<br />holdings one by one
                  </p>
                </Stack>
              </div>
            </Stack>
          </div>
        </Stack>
      </Callout>
    </div>

    <div v-if="showHoldingsArea" class="tw-flex tw-items-center tw-gap-3">
      <div
        class="holdings-date-header"
        style="display: inline-flex; align-items: center; gap: 0.5em"
      >
        <!-- Branch for non-editing mode -->
        <div
          v-if="!editingHoldingsDate"
          class="edit-hover-wrapper"
          style="display: inline-flex; align-items: center; gap: 0.5em"
        >
          <Subtitle> Holdings as of: {{ holdings_as_of_date }} </Subtitle>
          <!-- Only show edit button if hovering, not editing, user can edit, and holdings aren't synced -->
          <Button
            v-if="canEdit && !entity?.holdings_synced"
            @click="editHoldingsDate"
            class="edit-button"
            icon-only="fas fa-edit"
            variant="link"
            size="3"
          ></Button>
        </div>

        <!-- Branch for editing mode -->
        <Buttons v-else>
          <input
            type="date"
            v-model="editedHoldingsDate"
            class="stk-input"
          />
          <Button
            @click="saveHoldingsDate"
            variant="success"
            icon-before="fas fa-save"
          >
            Save
          </Button>
          <Button @click="cancelHoldingsDate" variant="danger">
            Cancel
          </Button>
        </Buttons>
      </div>
    </div>

    <SpreadsheetExport ref="exporter" />

    <PortfolioHoldingsArea
      v-if="showHoldingsArea"
      :can_edit="canEdit"
      :entity="entity"
      :show-replace-button="canEdit && hasHoldings"
      :show-add-button="canEdit && hasHoldings"
      :show-download-button="hasHoldings"
      :download-disabled="isDownloading"
      ref="holdingsArea"
      @replace-portfolio="showReplaceHoldingsModal = true"
      @add-portfolio="showAddHoldingsModal = true"
      @download="downloadHoldings"
      @holding-saved="handleHoldingSaved"
      @deleted-last-holding="handlePortfolioDeleted"
      @created-first-holding="handlePortfolioCreated"
    />

    <StakeModal v-model="showMultipleUploadInstructions">
      <Stack gap="2">
        <Title>Uploading to multiple members</Title>

        <p>
          To create multiple members, your CSV must
          include a column <code>Account</code> specifying
          which holdings belong to which members.<br />
          Each unique value in that column will represent a
          new member.
        </p>

        <img :src="`${$root.$data.STATIC_ROOT}images/portfolio/csv-upload-multiple.png`">
      </Stack>
    </StakeModal>

    <StakeModal v-model="showReplaceHoldingsModal" no-max-width>
      <Stack gap="4" class="tw-w-full tw-max-w-5xl">
        <div class="tw-mx-auto tw-text-center tw-space-y-2">
          <Title>Replace this portfolio</Title>
          <Subtitle>
            Choose <em>one</em> of the three methods below to replace holdings.
          </Subtitle>
        </div>

        <div
          class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4 tw-justify-items-center stk-color-text-normal"
        >
          <Stack
            gap="4"
            class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4"
          >
            <div class="tw-flex tw-items-center tw-gap-3">
              <div
                class="tw-bg-blue-100 tw-text-blue-600 tw-p-2 tw-rounded-full"
              >
                <Icon icon="fas fa-copy" />
              </div>
              <h3 class="tw-font-semibold">Copy Holdings from Portfolio</h3>
            </div>

            <div class="tw-flex-grow">
              <PortfolioSearchSelect
                placeholder="Search portfolios …"
                @selected="handleSelected"
                class="tw-w-full"
              />
            </div>

            <div v-if="copyPortfolioLoading">
              <div class="tw-flex tw-justify-center tw-items-center">
                Copying holdings from portfolio...
              </div>
              <br />
              <div class="tw-flex tw-justify-center tw-items-center">
                <i class="fa fa-spinner fa-spin fa-2x tw-text-gray-500"></i>
              </div>
            </div>
          </Stack>

          <div class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
            <UploadHoldingsCSV
              :entity="entity"
              :can-edit="canEdit"
              :before-upload="clearExistingHoldingsForReplace"
              @uploaded="handleHoldingsUploaded"
            />
          </div>

          <Stack
            gap="4"
            class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4"
          >
            <div class="tw-flex tw-items-center tw-gap-3">
              <div
                class="tw-bg-blue-100 tw-text-blue-600 tw-p-2 tw-rounded-full"
              >
                <Icon icon="fas fa-edit" />
              </div>
              <h3 class="tw-font-semibold">Add Manually</h3>
            </div>
            <div
              class="tw-flex-grow tw-flex tw-flex-col tw-items-center tw-justify-center"
            >
              <Stack gap="2" class="tw-text-center">
                <Button
                  @click="startManualEntryFromModal"
                  variant="primary"
                  size="3_5"
                  class="tw-w-full"
                >
                  Add Holdings Manually
                </Button>
                <p class="tw-text-sm tw-text-gray-600 tw-mb-4">
                  Create a new portfolio by adding<br />holdings one by one
                </p>
              </Stack>
            </div>
          </Stack>
        </div>
      </Stack>
    </StakeModal>

    <StakeModal v-model="showAddHoldingsModal" no-max-width>
      <Stack gap="4" class="tw-w-full tw-max-w-5xl">
        <div class="tw-mx-auto tw-text-center tw-space-y-2">
          <Title>Add to this portfolio</Title>
          <Subtitle>
            Choose <em>one</em> of the three methods below to add holdings.
          </Subtitle>
        </div>

        <div
          class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4 tw-justify-items-center stk-color-text-normal"
        >
          <Stack
            gap="4"
            class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4"
          >
            <div class="tw-flex tw-items-center tw-gap-3">
              <div
                class="tw-bg-blue-100 tw-text-blue-600 tw-p-2 tw-rounded-full"
              >
                <Icon icon="fas fa-copy" />
              </div>
              <h3 class="tw-font-semibold">Copy Holdings from Portfolio</h3>
            </div>

            <div class="tw-flex-grow">
              <PortfolioSearchSelect
                placeholder="Search portfolios …"
                @selected="handleAddSelected"
                class="tw-w-full"
              />
            </div>

            <div v-if="addPortfolioLoading">
              <div class="tw-flex tw-justify-center tw-items-center">
                Copying holdings from portfolio...
              </div>
              <br />
              <div class="tw-flex tw-justify-center tw-items-center">
                <i class="fa fa-spinner fa-spin fa-2x tw-text-gray-500"></i>
              </div>
            </div>
          </Stack>

          <div class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
            <UploadHoldingsCSV
              :entity="entity"
              :can-edit="canEdit"
              @uploaded="handleAddHoldingsUploaded"
            />
          </div>

          <Stack
            gap="4"
            class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4"
          >
            <div class="tw-flex tw-items-center tw-gap-3">
              <div
                class="tw-bg-blue-100 tw-text-blue-600 tw-p-2 tw-rounded-full"
              >
                <Icon icon="fas fa-edit" />
              </div>
              <h3 class="tw-font-semibold">Add Manually</h3>
            </div>
            <div
              class="tw-flex-grow tw-flex tw-flex-col tw-items-center tw-justify-center"
            >
              <Stack gap="2" class="tw-text-center">
                <Button
                  @click="startManualEntryFromAddModal"
                  variant="primary"
                  size="3_5"
                  class="tw-w-full"
                >
                  Add Holdings Manually
                </Button>
                <p class="tw-text-sm tw-text-gray-600 tw-mb-4">
                  Add more holdings to this portfolio<br />one by one
                </p>
              </Stack>
            </div>
          </Stack>
        </div>
      </Stack>
    </StakeModal>
  </Stack>
</template>

<script>
import * as Sentry from "@sentry/vue";
import PortfolioHoldingsArea from "@frontend/ui/features/checkup/components/PortfolioHoldingsArea.vue";
import PortfolioSearchSelect from "@frontend/ui/features/checkup/components/portfolio-select/PortfolioSearchSelect.vue";
import SpreadsheetExport from "@frontend/ui/features/checkup/components/SpreadsheetExport.vue";
import { useCheckupStore } from "@frontend/ui/stores/checkupStore";
import { mapStores } from "pinia";
import {
  Button,
  Buttons,
  LoaderWithText,
  Stack,
  Icon,
  StakeModal,
  Dropzone,
  Callout,
  TextLink,
  Title,
  Subtitle,
} from "@stakeui";
import UploadHoldingsCSV from "../../holdings/UploadHoldingsCSV.vue";

export default {
  name: "EntityHoldings",
  components: {
    PortfolioHoldingsArea,
    PortfolioSearchSelect,
    SpreadsheetExport,
    Button,
    Buttons,
    Subtitle,
    LoaderWithText,
    Stack,
    StakeModal,
    Dropzone,
    Callout,
    TextLink,
    Title,
    Icon,
    UploadHoldingsCSV,
  },
  props: {
    entityExternalId: { type: String, required: true },
    entity: { type: Object, required: true },
  },
  data() {
    return {
      hasFetchedHoldings: false,
      isFetchingHoldings: true,
      entityUploadedDfAgg: [],
      showMultipleUploadInstructions: false,
      holdings_as_of_date: this.entity ? this.entity.holdings_as_of_date : null,
      editingHoldingsDate: false,
      editedHoldingsDate: null,
      saveMessage: "",
      uploadName: "",
      showHoldingsArea: false,
      copyPortfolioLoading: false,
      csvUploadLoading: false,
      csvUploadTarget: "direct",
      csvUploadNewMember: "",
      showReplaceHoldingsModal: false,
      showAddHoldingsModal: false,
      addPortfolioLoading: false,
      isDownloading: false,
    };
  },
  async created() {
    await this.populateEntityHoldings(this.entityExternalId);
  },
  computed: {
    ...mapStores(useCheckupStore),
    headers() {
      return {
        "X-CSRFToken": this.$root.$data.csrfToken,
        "Content-Type": "application/json",
      };
    },
    canEdit() {
      return (
        this.entity &&
        this.entity.user_permissions &&
        this.entity.user_permissions.includes("change_entity")
      );
    },
    hasHoldings() {
      return this.entityUploadedDfAgg && this.entityUploadedDfAgg.length > 0;
    },
  },
  watch: {
    entityUploadedDfAgg(newVal) {
      if (newVal && newVal.length) {
        this.showHoldingsArea = true;
      }
    },
  },
  methods: {
    startManualEntry() {
      /* show the table */
      this.showHoldingsArea = true;

      /* wait until it has rendered, then call the child’s method */
      this.$nextTick(() => {
        const child = this.$refs.holdingsArea;
        if (child && typeof child.addNewHolding === "function") {
          child.addNewHolding();
        }
      });
    },
    startManualEntryFromModal() {
      this.clearExistingHoldingsForReplace().then((cleared) => {
        if (!cleared) return;
        this.showReplaceHoldingsModal = false;
        this.startManualEntry();
      });
    },
    startManualEntryFromAddModal() {
      this.showAddHoldingsModal = false;
      this.startManualEntry();
    },
    async clearExistingHoldingsForReplace() {
      if (!this.hasHoldings) return true;
      const url = `/api/v1/entities/${this.entityExternalId}/dereference-holdings/`;
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: { ...this.headers },
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to replace holdings.");
        }
        await this.populateEntityHoldings(this.entityExternalId);
        return true;
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error replacing holdings:", error);
        return false;
      }
    },
    handleHoldingSaved(newHolding) {
      if (this.entityUploadedDfAgg.length === 0) {
        this.entityUploadedDfAgg.push(newHolding);
      }
    },
    async populateEntityHoldings(externalId, update = true) {
      this.isFetchingHoldings = true;
      const url = `/api/v1/entities/${externalId}/holdings`;
      try {
        const res = await fetch(url, {
          method: "GET",
          headers: { ...this.headers },
        });
        const data = await res.json();
        this.entityUploadedDfAgg = data.portfolio.uploaded_df_agg;
        if (this.entityUploadedDfAgg && update) {
          const portfolio = await this.checkupStore.getEntityLight(
            this.entityExternalId
          );
          this.checkupStore.addPortfolio({ ...portfolio }, "portfolio");
          await this.checkupStore.updateHoldingsByPortfolio();
          this.$emit("refetch-entity");
        }
      } catch (error) {
      Sentry.captureException(error);
        console.error("Error fetching holdings:", error);
      } finally {
        this.hasFetchedHoldings = true;
        this.isFetchingHoldings = false;
      }
    },
    handlePortfolioDeleted() {
      this.entityUploadedDfAgg = [];
      this.showHoldingsArea = false;
      this.$emit("refetch-entity");
    },
    handlePortfolioCreated() {
      this.$emit("refetch-entity");
    },
    editHoldingsDate() {
      this.editingHoldingsDate = true;
      this.editedHoldingsDate = this.holdings_as_of_date;
    },
    cancelHoldingsDate() {
      this.editingHoldingsDate = false;
      this.editedHoldingsDate = this.holdings_as_of_date;
    },
    async saveHoldingsDate() {
      try {
        const url = `/api/v1/entities/${this.entityExternalId}/update-entity-field/`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            ...this.headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            holdings_as_of_date: this.editedHoldingsDate,
          }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Update failed.");
        }
        this.holdings_as_of_date = this.editedHoldingsDate;
        this.editingHoldingsDate = false;
        this.saveMessage = "Holdings date updated successfully!";
        setTimeout(() => {
          this.saveMessage = "";
        }, 1500);
      } catch (error) {
        console.error("Error updating holdings date:", error);
        this.saveMessage =
          error.message || "Failed to update holdings date. Try again.";
      }
    },
    async handleSelected(portfolio) {
      if (!this.canEdit) return;

      this.copyPortfolioLoading = true;
      const cleared = await this.clearExistingHoldingsForReplace();
      if (!cleared) {
        this.copyPortfolioLoading = false;
        return;
      }
      const url = `/api/v1/copy-holdings-to-entity/${this.entityExternalId}/${portfolio.external_id}/`;
      await fetch(url, {
        method: "POST",
        headers: { ...this.headers },
      });
      this.populateEntityHoldings(this.entityExternalId);
      this.showReplaceHoldingsModal = false;
      this.copyPortfolioLoading = false;
    },
    async handleAddSelected(portfolio) {
      if (!this.canEdit) return;

      this.addPortfolioLoading = true;
      try {
        const url = `/api/v1/copy-holdings-to-entity/${this.entityExternalId}/${portfolio.external_id}/`;
        const response = await fetch(url, {
          method: "POST",
          headers: { ...this.headers },
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to add holdings.");
        }
        this.populateEntityHoldings(this.entityExternalId);
        this.showAddHoldingsModal = false;
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error adding holdings:", error);
      } finally {
        this.addPortfolioLoading = false;
      }
    },
    handleHoldingsUploaded(externalId) {
      this.showReplaceHoldingsModal = false;
      this.populateEntityHoldings(externalId);
    },
    handleAddHoldingsUploaded(externalId) {
      this.showAddHoldingsModal = false;
      this.populateEntityHoldings(externalId);
    },
    async downloadHoldings() {
      if (this.isDownloading) return;
      this.isDownloading = true;
      try {
        const res = await fetch(
          `/api/v1/entities/${this.entityExternalId}/holdings`,
          { method: "GET", headers: { ...this.headers } },
        );
        if (!res.ok) throw new Error("Failed to fetch latest holdings.");
        const data = await res.json();
        const latestHoldings = data.portfolio.uploaded_df_agg;
        if (!latestHoldings?.length) return;
        const exporter = this.$refs.exporter;
        const formatted = latestHoldings.map((h) => exporter.formatHoldingRow(h));
        const baseName = (this.entity?.name || "holdings").replace(/[^a-z0-9_-]+/gi, "_");
        const result = await exporter.createSpreadsheet(baseName, formatted);
        if (!result) return;
        const link = document.createElement("a");
        link.href = result.url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(result.url);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error downloading holdings:", error);
      } finally {
        this.isDownloading = false;
      }
    },
  },
};
</script>

<style scoped>
.save-message {
  margin-bottom: 1em;
  color: green;
  font-weight: bold;
}

/* Lighten the Callout background to a very pale blue */
.tw-bg-blue-50 {
  background-color: #f0f9ff;
}
.instructions {
  padding: 1.25em 1.5em;
}
.cols {
  display: grid;
  grid-template-columns: 1fr 4em 1fr;
}
.or {
  text-align: center;
}
/* Additional styling for the holdings date header and edit button */
.holdings-date-header {
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
}

.edit-button {
  opacity: 0;
}

.edit-hover-wrapper:hover .edit-button {
  opacity: 1;
}
</style>
